import React from 'react';
import { TooltipProvider } from '@onward-delivery/design';

import BucketTabs from './BucketTabs';
import OnboardingFilters from './OnboardingFilters';
import OnboardingRow from './OnboardingRow';
import PanelMessage from './PanelMessage';
import ReviewDialog from './ReviewDialog';
import ReviewHistoryDialog from './ReviewHistoryDialog';
import SelectionPanel from './SelectionPanel';
import { useCarrierOnboarding } from '../hooks';
import { approveSkipReason, selectionActions } from '../utils';

/**
 * The Carriers onboarding view: the four tabs, the active tab's cards, the options panel for checked cards, and the
 * review dialogs.
 *
 * @param {{ search: string }} props
 */
const CarrierOnboarding = ({ search }) => {
    const {
        loading,
        error,
        hasOrganization,
        bucket,
        setBucketKey,
        filters,
        setFilters,
        counts,
        bucketRows,
        selectedIds,
        selectedRows,
        toggleRow,
        clearSelection,
        busy,
        reinvite,
        setIntegrationsRequired,
        startReview,
        reviewQueue,
        submitReview,
        skipReview,
        cancelReview,
        viewing,
        closeViewing,
        runMenuOption,
    } = useCarrierOnboarding({ search });

    const { invitable, reviewable, approvable, markNoIntegrations, requireIntegrations } = selectionActions(
        selectedRows,
        bucket.key
    );
    const panelButtons = [
        {
            key: 'INVITE',
            label: 'Invite',
            variant: 'outline',
            show: invitable.length > 0,
            onClick: () => reinvite(invitable, selectedRows.length - invitable.length),
        },
        {
            key: 'NO_INTEGRATIONS',
            label: 'Mark No Integrations Required',
            variant: 'outline',
            show: markNoIntegrations.length > 0,
            onClick: () => setIntegrationsRequired(markNoIntegrations, false),
        },
        {
            // Only flagged carriers get this, and they're never on the Integrations tab with the button above.
            key: 'REQUIRE_INTEGRATIONS',
            label: 'Require Integrations',
            variant: 'outline',
            show: requireIntegrations.length > 0,
            sizes: false,
            onClick: () => setIntegrationsRequired(requireIntegrations, true),
        },
        {
            key: 'DENY',
            label: 'Deny',
            variant: 'destructive',
            show: reviewable.length > 0,
            onClick: () => startReview('DENIED', reviewable),
        },
        {
            key: 'APPROVE',
            label: 'Approve',
            variant: 'primary',
            show: reviewable.length > 0,
            noneEligible: approvable.length === 0,
            reason: approveSkipReason(reviewable),
            onClick: () => startReview('APPROVED', approvable),
        },
    ].map(({ noneEligible, ...button }) => ({ ...button, disabled: busy || !!noneEligible }));

    let body;
    if (!hasOrganization) body = <PanelMessage>Your account is not associated with an organization.</PanelMessage>;
    else if (error) body = <PanelMessage>Couldn&apos;t load onboarding. Try refreshing the page.</PanelMessage>;
    else if (loading) body = <PanelMessage>Loading…</PanelMessage>;
    else if (!bucketRows.length) {
        const filtered = filters.types.length || filters.badges.length;
        body = <PanelMessage>{filtered ? 'No carriers here match the filters.' : 'No carriers here.'}</PanelMessage>;
    } else {
        body = (
            <ul className="flex flex-col gap-2.5">
                {bucketRows.map((row) => (
                    <OnboardingRow
                        key={row.carrier_id}
                        row={row}
                        checked={selectedIds.has(row.carrier_id)}
                        onToggle={toggleRow}
                        onMenuOption={runMenuOption}
                    />
                ))}
            </ul>
        );
    }

    // Radix tooltips throw without a provider above them: the Approve reason and the compliance badge notes. Radix
    // waits 700ms by default before opening; a shorter delay makes a hover over a badge feel responsive.
    return (
        <TooltipProvider delayDuration={200}>
            <div className="flex min-h-0 w-full flex-1 flex-col">
                <BucketTabs activeKey={bucket.key} counts={counts} onSelect={setBucketKey} />
                <section
                    className={`flex min-h-0 flex-1 flex-col rounded-b-lg rounded-tr-lg border ${bucket.panelClass}`}
                >
                    <div className="min-h-0 flex-1 overflow-y-auto p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <p className="text-[13px] text-slate-600">{bucket.subtitle}</p>
                            <OnboardingFilters filters={filters} onChange={setFilters} />
                        </div>
                        {body}
                        {selectedRows.length ? (
                            <SelectionPanel
                                count={selectedRows.length}
                                buttons={panelButtons}
                                onClear={clearSelection}
                            />
                        ) : null}
                    </div>
                </section>
                <ReviewDialog
                    queue={reviewQueue}
                    busy={busy}
                    onSubmit={submitReview}
                    onSkip={skipReview}
                    onCancel={cancelReview}
                />
                <ReviewHistoryDialog viewing={viewing} onClose={closeViewing} />
            </div>
        </TooltipProvider>
    );
};

export default CarrierOnboarding;
